import React from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { Modal, PrimaryButton } from '../../components';
import { NamedLink } from '../../components';
import {
  TIMED_SALE_BUYER_COMMISSION_PERCENTAGE,
  formatBidMoney,
  getBidPricingBreakdown,
  getBidUnitLabels,
  getListingBidQuantity,
} from '../../util/bidPricing';
import css from './ListingPage.module.css';

const BID_AMOUNT_INPUT_ID = 'placeBidAmountInput';

const getCurrencySymbol = (intl, currency) => {
  try {
    const locale = intl?.locale || undefined;
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    return parts.find(part => part.type === 'currency')?.value || currency;
  } catch (e) {
    return currency;
  }
};

const BidModal = ({
  isOpen,
  onClose,
  listing,
  bidModalPayload,
  bidInput,
  setBidInput,
  bidError,
  bidInProgress,
  handleConfirmBid,
  intl,
  config,
  onManageDisableScrolling,
}) => {
  const currency = bidModalPayload?.marketplaceCurrency || config.currency;
  const unitLabels = getBidUnitLabels(listing);
  const quantity = bidModalPayload?.quantity || getListingBidQuantity(listing);
  const bidBreakdown = getBidPricingBreakdown({ bidInput, currency, quantity });
  const formattedUnitPrice = formatBidMoney(intl, bidBreakdown.unitPrice);
  const formattedTotal = formatBidMoney(intl, bidBreakdown.total);
  const formattedHammerPrice = formatBidMoney(intl, bidBreakdown.hammerPrice);
  const formattedBuyerCommission = formatBidMoney(
    intl,
    bidBreakdown.timedSaleFees?.buyerCommission
  );
  const formattedBuyerTotal = formatBidMoney(intl, bidBreakdown.timedSaleFees?.buyerTotal);
  const quantityUnitLabel = bidBreakdown.quantity === 1 ? unitLabels.singular : unitLabels.plural;
  const currencySymbol = getCurrencySymbol(intl, currency);
  const showBidBreakdown = bidBreakdown.showTotal || bidBreakdown.showTimedSaleFees;

  return (
    <Modal
      id="PlaceBidModal"
      containerClassName={css.bidModalContainer}
      contentClassName={css.bidModalContent}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      focusElementId={BID_AMOUNT_INPUT_ID}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'RequestChangesModal.close' })}
      modalTitle=<FormattedMessage id="OrderPanel.placeBid" defaultMessage="Place Bid" />
    >
      <div className={css.bidInputSection}>
        <label className={css.bidInputLabel} htmlFor={BID_AMOUNT_INPUT_ID}>
          <FormattedMessage
            id="OrderPanel.bidPricePerUnitLabel"
            defaultMessage="Bid price per {unitLabel}"
            values={{ unitLabel: unitLabels.singular }}
          />{' '}
          ({currency})
        </label>
        <div className={css.bidInput}>
          <span className={css.prefixIcon}>{currencySymbol}</span>
          <input
            id={BID_AMOUNT_INPUT_ID}
            type="number"
            inputMode="decimal"
            min={
              bidModalPayload?.minimumBidMoney?.amount != null
                ? bidModalPayload.minimumBidMoney.amount / 100
                : 0
            }
            step="0.01"
            value={bidInput}
            onChange={e => setBidInput(e.target.value)}
            placeholder={
              bidModalPayload?.minimumBidMoney?.amount != null
                ? String(bidModalPayload.minimumBidMoney.amount / 100)
                : '0'
            }
            disabled={bidInProgress || bidModalPayload?.isEnded}
          />
        </div>

        {showBidBreakdown ? (
          <div className={css.bidBreakdown} aria-live="polite">
            {bidBreakdown.showTotal ? (
              <>
                <div className={css.bidBreakdownRow}>
                  <span>
                    <FormattedMessage
                      id="OrderPanel.bidQuantityBreakdownLabel"
                      defaultMessage="Quantity"
                    />
                  </span>
                  <strong>
                    <FormattedMessage
                      id="OrderPanel.bidQuantityBreakdownValue"
                      defaultMessage="{quantity} {unitLabel}"
                      values={{ quantity: bidBreakdown.quantity, unitLabel: quantityUnitLabel }}
                    />
                  </strong>
                </div>
                <div className={css.bidBreakdownRow}>
                  <span>
                    <FormattedMessage
                      id="OrderPanel.bidUnitPriceBreakdownLabel"
                      defaultMessage="Bid price per {unitLabel}"
                      values={{ unitLabel: unitLabels.singular }}
                    />
                  </span>
                  <strong>{formattedUnitPrice}</strong>
                </div>
              </>
            ) : null}
            <div
              className={classNames(css.bidBreakdownRow, {
                [css.bidBreakdownTotalRow]: !bidBreakdown.showTotal,
              })}
            >
              <span>
                <FormattedMessage
                  id={
                    bidBreakdown.showTotal
                      ? 'OrderPanel.bidTotalBreakdownLabel'
                      : 'OrderPanel.auctionHammerPriceLabel'
                  }
                  defaultMessage={bidBreakdown.showTotal ? 'Total bid amount' : 'Hammer price'}
                />
              </span>
              <strong>
                {bidBreakdown.showTotal ? (
                  <FormattedMessage
                    id="OrderPanel.bidTotalBreakdownValue"
                    defaultMessage="{unitPrice} x {quantity} = {total}"
                    values={{
                      unitPrice: formattedUnitPrice,
                      quantity: bidBreakdown.quantity,
                      total: formattedTotal,
                    }}
                  />
                ) : (
                  formattedHammerPrice
                )}
              </strong>
            </div>
            {bidBreakdown.showTimedSaleFees ? (
              <>
                <div className={css.bidBreakdownRow}>
                  <span>
                    <FormattedMessage
                      id="OrderPanel.auctionBuyerCommissionLabel"
                      defaultMessage="Buyer commission ({percentage}%)"
                      values={{ percentage: TIMED_SALE_BUYER_COMMISSION_PERCENTAGE }}
                    />
                  </span>
                  <strong>{formattedBuyerCommission}</strong>
                </div>
                <div className={classNames(css.bidBreakdownRow, css.bidBreakdownTotalRow)}>
                  <span>
                    <FormattedMessage
                      id="OrderPanel.auctionBuyerTotalLabel"
                      defaultMessage="Estimated buyer total"
                    />
                  </span>
                  <strong>{formattedBuyerTotal}</strong>
                </div>
              </>
            ) : null}
          </div>
        ) : null}

        {bidError ? <div className={css.errorText}>{bidError}</div> : null}

        <div className={css.bidModalActions}>
          {/* <InlineTextButton onClick={onClose} className={css.cancelBidBtn}>
            <FormattedMessage id="OrderPanel.cancel" defaultMessage="Cancel" />
          </InlineTextButton> */}

          <PrimaryButton
            onClick={handleConfirmBid}
            inProgress={bidInProgress}
            disabled={bidInProgress || bidModalPayload?.isEnded}
          >
            <FormattedMessage id="OrderPanel.confirmBid" defaultMessage="Confirm Bid" />
          </PrimaryButton>
        </div>

        <div className={css.termsWrapper}>
          <FormattedMessage
            id="OrderPanel.byBidding"
            defaultMessage="By bidding, you agree to the"
          />{' '}
          <NamedLink name="TermsOfServicePage">
            <FormattedMessage id="OrderPanel.termsOfSale" defaultMessage="Terms of Sale" />
          </NamedLink>
          .
        </div>
      </div>
    </Modal>
  );
};

export default BidModal;
